/**
 * PageHeader Component
 *
 * Context-aware header for dashboard pages.
 * PURELY PRESENTATIONAL - displays title, breadcrumbs, and actions.
 *
 * @example
 * ```tsx
 * <PageHeader
 *   title="Active Jobs"
 *   description="Manage your current job assignments"
 *   breadcrumbs={[
 *     { label: 'Dashboard', href: '/provider/dashboard' },
 *     { label: 'Jobs' }
 *   ]}
 *   actions={[
 *     { label: 'New Job', variant: 'primary', onClick: handleNewJob }
 *   ]}
 * />
 * ```
 */

import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import type { PageHeaderViewModel } from './types';

export interface PageHeaderProps extends PageHeaderViewModel {
  /** Optional className for custom styling */
  className?: string;
}

/**
 * Page header with title, breadcrumbs, and action buttons.
 * Responsive layout adjusts for mobile screens.
 */
export function PageHeader({
  title,
  description,
  breadcrumbs,
  actions,
  className = '',
}: PageHeaderProps) {
  return (
    <div className={`px-4 md:px-6 lg:px-8 py-6 ${className}`}>
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav className="mb-2" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-2 text-sm">
            {breadcrumbs.map((crumb, index) => (
              <li key={index} className="flex items-center">
                {index > 0 && (
                  <svg
                    className="w-4 h-4 text-neutral-400 mx-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
                {crumb.href ? (
                  <Link
                    to={crumb.href}
                    className="text-neutral-600 hover:text-neutral-900 transition-colors"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-neutral-900 font-medium">
                    {crumb.label}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}

      {/* Title and Actions Row */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        {/* Title Section */}
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl md:text-3xl font-bold text-neutral-900 truncate">
            {title}
          </h1>
          {description && (
            <p className="mt-1 text-sm md:text-base text-neutral-600">
              {description}
            </p>
          )}
        </div>

        {/* Actions Section */}
        {actions && actions.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {actions.map((action, index) => (
              <Button
                key={index}
                variant={
                  action.variant === 'primary'
                    ? 'default'
                    : action.variant === 'secondary'
                    ? 'secondary'
                    : 'outline'
                }
                onClick={action.onClick}
                disabled={action.disabled}
                className="flex items-center gap-2"
              >
                {action.icon && <span className="w-4 h-4">{action.icon}</span>}
                <span>{action.label}</span>
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
